import omit from 'lodash/omit';
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import uuid from 'uuid/v4';

import { ACTION, EVENT_TYPE } from '@atlaskit/editor-common/analytics';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { createUnifiedAnalyticsPayload } from '@atlassian/editor-ai-common/analytics/create-unified-analytics-payload';

import type { AIPlugin } from '../../aiPluginType';
import { convertExperienceName } from '../../ui/utils/analytics/analyticsFlow';
import { addAnalytics } from '../../ui/utils/analytics/utils';
import { aiExperienceDecorationPluginKey } from '../decoration/decoration-plugin-key';

import { aiMonitorWatchmenPluginKey } from './ai-monitor-watchmen-plugin-key';
import type {
	AIMonitorWatchmenPluginState,
	CommonAttributes,
	DeferredAnalyticsState,
} from './ai-monitor-watchmen-plugin-key';
import type { Session } from './types';
import {
	collectChangedRangesFromMapping,
	countSessionReductions,
	finishImmediateReaction,
	isExactInverseOfSession,
	isRedoOfUndoneSession,
	mergeRanges,
	sumTextCounts,
	txnTouchesAiRanges,
} from './utils';

const META_FLUSH_ANALYTICS = 'AIMonitorWatchmenFlushAnalytics';
const META_DEFERRED_ANALYTICS = 'AIMonitorWatchmenDeferredAnalytics';
export const META_UPDATE_COMMON_ATTRIBUTES = 'AIMonitorWatchmenCommonAttributes';
const ACTIONED_EVENT_DELAY = 30000;

const initialState: AIMonitorWatchmenPluginState = {
	currentSession: null,
	pendingAnalytics: null,
	lastUndoneSession: null,
	deferredAnalytics: { deferred: {}, sessionState: 'notStarted' },
	commonAttributes: {},
};

export const createAIMonitorWatchmenPlugin = (
	api: ExtractInjectionAPI<AIPlugin> | undefined,
	autoApplyAIChangesEnabled: () => boolean,
): SafePlugin => {
	return new SafePlugin<AIMonitorWatchmenPluginState>({
		key: aiMonitorWatchmenPluginKey,
		state: {
			init: () => initialState,
			apply: (tr, pluginState, _old, newState) => {
				let sessionStarted = false;
				const decorationPluginState = aiExperienceDecorationPluginKey.getState(newState);
				sessionStarted = decorationPluginState?.sessionStarted ?? false;
				const { deferredAnalytics } = pluginState;

				if (sessionStarted && deferredAnalytics.sessionState !== 'inProgress') {
					return {
						...pluginState,
						deferredAnalytics: { ...deferredAnalytics, sessionState: 'inProgress' },
					};
				}

				const sessionEnded = decorationPluginState?.sessionEnded ?? false;
				if (sessionEnded && deferredAnalytics.sessionState === 'inProgress') {
					if (tr.getMeta(aiExperienceDecorationPluginKey)?.data?.type === 'finished') {
						return {
							...pluginState,
							deferredAnalytics: { ...deferredAnalytics, sessionState: 'justEnded' },
						};
					} else {
						return {
							...pluginState,
							deferredAnalytics: {
								...deferredAnalytics,
								toCancel: decorationPluginState?.singleInstrumentationId,
							},
						};
					}
				}

				const deferredAnalyticsFromMeta: DeferredAnalyticsState | undefined =
					tr.getMeta(META_DEFERRED_ANALYTICS);

				if (deferredAnalyticsFromMeta) {
					return { ...pluginState, deferredAnalytics: deferredAnalyticsFromMeta };
				}

				const updatedCommonAttributes: CommonAttributes | undefined = tr.getMeta(
					META_UPDATE_COMMON_ATTRIBUTES,
				);

				if (updatedCommonAttributes) {
					return { ...pluginState, commonAttributes: updatedCommonAttributes };
				}

				if (tr.getMeta(META_FLUSH_ANALYTICS)) {
					return { ...pluginState, pendingAnalytics: null };
				}

				const isCollab = Boolean(tr.getMeta('isRemote'));
				const isFinalAI = Boolean(tr.getMeta('isAIFinalStreamingTransformation'));
				const appendedTr = tr.getMeta('appendedTransaction');
				const isIntermediateAI = Boolean(
					tr.getMeta('isAIStreamingTransformation') ||
					(appendedTr && appendedTr.getMeta('isAIStreamingTransformation') && !isFinalAI),
				);

				const aiDecorationExperiencePluginState =
					api?.aiExperience.sharedState.currentState()?.aiDecorationExperiencePluginState;
				const configItemKey = aiDecorationExperiencePluginState?.configItem?.key || '';
				const invokedFrom = aiDecorationExperiencePluginState?.lastTriggeredFrom;
				const experienceName = convertExperienceName(configItemKey, invokedFrom);

				// ignore txns that don't change the doc
				if (!tr.docChanged || tr.getMeta('metricsPlugin$')) {
					return pluginState;
				}

				// start a new analytic session when a stream finalizes
				if (isFinalAI) {
					const changed = collectChangedRangesFromMapping(tr.mapping.maps);
					const mergedNewRanges = mergeRanges(
						changed.map((r) => ({ from: r.newStart, to: r.newEnd })).filter((r) => r.from < r.to),
					);
					const { chars, words } = sumTextCounts(newState.doc, mergedNewRanges);
					const decorationPluginState = aiExperienceDecorationPluginKey.getState(newState);

					const session: Session = {
						// If `singleInstrumentationId` exists then we've been invoked by a free gen session,
						// otherwise we use `interactionSessionId` which comes from quick actions.
						// If none of these are available we will need to query for the `empty` events
						// Note we always need a valid ID
						id:
							decorationPluginState?.singleInstrumentationId ??
							decorationPluginState?.interactionSessionId ??
							// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
							`empty_${uuid()}`,
						startedAt: Math.round(performance.now()),
						ranges: changed,
						mergedNewRanges,
						baselineDoc: newState.doc,
						aiOutputChars: chars,
						aiOutputWords: words,
						awaitingNextAction: true,
						aiGroupId: decorationPluginState?.nextSessionId,
						experienceName,
					};

					let updatedDeferredAnalytics = pluginState.deferredAnalytics;

					if (autoApplyAIChangesEnabled()) {
						/**
						 * If traceId is present in decoration plugin state, use it, otherwise use traceIds from common attributes.
						 * Currently, traceId is set in decoration plugin state only for freegen.
						 * For other prompts, it should be in common attributes.
						 */
						const traceIds = decorationPluginState?.traceId
							? [decorationPluginState.traceId]
							: pluginState.commonAttributes.traceIds;
						const promptVersion = fg('platform_editor_ai_promptversion_in_analytics')
							? decorationPluginState?.promptVersion
							: undefined;
						const actionedPayload = createUnifiedAnalyticsPayload({
							...pluginState.commonAttributes,
							action: ACTION.ACTIONED,
							attributes: {
								aiResultAction: 'accepted',
								aiActionedType: 'automaticallyInserted',
							},
							interactionID: session.id,
							experienceName,
							invokedFrom,
							aiGroupId: decorationPluginState?.nextSessionId,
							isPayloadCreate: decorationPluginState?.isCreate,
							...(fg('platform_editor_ai_traceid_in_analytics_events') && { traceIds }),
							...(promptVersion ? { promptVersion } : {}),
						});

						updatedDeferredAnalytics = {
							...pluginState.deferredAnalytics,
							toDefer: {
								aiSessionId: session.id,
								payload: actionedPayload,
								waitForSessionEnd: pluginState.deferredAnalytics.sessionState === 'inProgress',
							},
						};
					}

					return {
						...pluginState,
						currentSession: session,
						pendingAnalytics: null,
						deferredAnalytics: updatedDeferredAnalytics,
					};
				}

				// observe and classify the very next local user action
				if (
					pluginState.currentSession &&
					pluginState.currentSession.awaitingNextAction &&
					!isCollab &&
					tr.docChanged
				) {
					const s = pluginState.currentSession;

					const isRegen = isIntermediateAI;

					if (isRegen) {
						return finishImmediateReaction(pluginState, s, 'regen', newState.doc);
					}

					const isUndo = isExactInverseOfSession(tr.mapping.maps, s.ranges);

					if (isUndo) {
						// Store the undone session for potential redo detection
						const undoneSession = {
							aiSessionId: s.id,
							undoneRanges: s.ranges,
							aiOutputChars: s.aiOutputChars,
							aiOutputWords: s.aiOutputWords,
							aiGroupId: s.aiGroupId,
							experienceName: s.experienceName,
							undoneAt: Math.round(performance.now()),
						};

						let updatedDeferredAnalytics = pluginState.deferredAnalytics;

						updatedDeferredAnalytics = { ...pluginState.deferredAnalytics, toCancel: s.id };

						return {
							...finishImmediateReaction(pluginState, s, 'undo', newState.doc, {
								removedChars: s.aiOutputChars,
								removedWords: s.aiOutputWords,
								touched: s.mergedNewRanges.length,
							}),
							deferredAnalytics: updatedDeferredAnalytics,
							lastUndoneSession: undoneSession,
						};
					}

					if (!txnTouchesAiRanges(tr.mapping.maps, s.mergedNewRanges)) {
						return finishImmediateReaction(pluginState, s, 'other', newState.doc);
					}

					const { deletedChars, deletedWords, touched } = countSessionReductions(
						tr.steps,
						tr.mapping.maps,
						s.baselineDoc,
						s.mergedNewRanges,
					);
					const isDelete = deletedChars > 0 || deletedWords > 0;

					const overHalfDeleted = deletedChars >= s.aiOutputChars / 2;

					let updatedDeferredAnalytics = pluginState.deferredAnalytics;

					if (overHalfDeleted) {
						updatedDeferredAnalytics = { ...pluginState.deferredAnalytics, toCancel: s.id };
					}

					return {
						...finishImmediateReaction(
							pluginState,
							s,
							isDelete ? 'delete' : 'other',
							newState.doc,
							{
								removedChars: deletedChars,
								removedWords: deletedWords,
								touched,
							},
							overHalfDeleted,
						),
						deferredAnalytics: updatedDeferredAnalytics,
					};
				}

				// Check for redo of previously undone AI content
				if (pluginState.lastUndoneSession && !isCollab && tr.docChanged && !isIntermediateAI) {
					const undoneSession = pluginState.lastUndoneSession;
					const now = Math.round(performance.now());
					const timeSinceUndo = now - undoneSession.undoneAt;

					// Check if this is a redo within a reasonable time window (2000ms like undo)
					if (timeSinceUndo <= 2000) {
						const isRedo = isRedoOfUndoneSession(tr.mapping.maps, undoneSession.undoneRanges);

						if (isRedo) {
							// Create a mock session for analytics purposes
							const mockSession: Session = {
								id: undoneSession.aiSessionId,
								startedAt: undoneSession.undoneAt,
								ranges: undoneSession.undoneRanges,
								mergedNewRanges: undoneSession.undoneRanges.map((r) => ({
									from: r.newStart,
									to: r.newEnd,
								})),
								baselineDoc: newState.doc,
								aiOutputChars: undoneSession.aiOutputChars,
								aiOutputWords: undoneSession.aiOutputWords,
								awaitingNextAction: false,
								aiGroupId: undoneSession.aiGroupId,
								experienceName: undoneSession.experienceName,
							};

							let updatedDeferredAnalytics = pluginState.deferredAnalytics;

							if (autoApplyAIChangesEnabled()) {
								/**
								 * If traceId is present in decoration plugin state, use it, otherwise use traceIds from common attributes.
								 * Currently, traceId is set in decoration plugin state only for freegen.
								 * For other prompts, it should be in common attributes.
								 */
								const traceIds = decorationPluginState?.traceId
									? [decorationPluginState.traceId]
									: pluginState.commonAttributes.traceIds;
								const promptVersion = fg('platform_editor_ai_promptversion_in_analytics')
									? decorationPluginState?.promptVersion
									: undefined;
								const actionedPayload = createUnifiedAnalyticsPayload({
									...pluginState.commonAttributes,
									action: ACTION.ACTIONED,
									attributes: {
										aiResultAction: 'accepted',
										aiActionedType: 'automaticallyInserted',
										wasRedone: true,
									},
									interactionID: undoneSession.aiSessionId,
									experienceName: undoneSession.experienceName,
									invokedFrom,
									aiGroupId: undoneSession.aiGroupId,
									...(fg('platform_editor_ai_traceid_in_analytics_events') && { traceIds }),
									...(promptVersion ? { promptVersion } : {}),
								});

								updatedDeferredAnalytics = {
									...pluginState.deferredAnalytics,
									toDefer: {
										aiSessionId: undoneSession.aiSessionId,
										payload: actionedPayload,
										waitForSessionEnd: pluginState.deferredAnalytics.sessionState === 'inProgress',
									},
								};
							}

							return {
								...finishImmediateReaction(pluginState, mockSession, 'redo', newState.doc, {
									removedChars: undoneSession.aiOutputChars,
									removedWords: undoneSession.aiOutputWords,
									touched: undoneSession.undoneRanges.length,
								}),
								lastUndoneSession: null, // Clear after redo
								deferredAnalytics: updatedDeferredAnalytics,
							};
						}
					} else {
						// Clear expired undone session
						return { ...pluginState, lastUndoneSession: null };
					}
				}

				return pluginState;
			},
		},

		appendTransaction: (_transactions, _oldState, newState) => {
			const ps = aiMonitorWatchmenPluginKey.getState(newState);
			const decorationPluginState = aiExperienceDecorationPluginKey.getState(newState);
			const pluginState = ps ?? initialState;
			const deferredAnalytics = pluginState.deferredAnalytics;

			const { toDefer, toCancel, sessionState } = deferredAnalytics;
			const waitForSessionEnd = toDefer?.waitForSessionEnd;
			const readyToDefer = !waitForSessionEnd || sessionState === 'justEnded';

			if (toDefer && readyToDefer) {
				const timeoutId = setTimeout(
					() => api?.analytics?.actions.fireAnalyticsEvent(toDefer.payload),
					ACTIONED_EVENT_DELAY,
				);

				const updatedDeferredAnalytics = {
					...deferredAnalytics,
					deferred: {
						...deferredAnalytics?.deferred,
						[toDefer.aiSessionId]: timeoutId,
					},
					toDefer: undefined,
					sessionState: 'notStarted',
				};

				return newState.tr.setMeta(META_DEFERRED_ANALYTICS, updatedDeferredAnalytics);
			}

			if (toCancel) {
				if (expValEquals('platform_editor_discarded_unified_analytic_events', 'isEnabled', true)) {
					const discardedPayload = createUnifiedAnalyticsPayload({
						...pluginState.commonAttributes,
						action: ACTION.DISCARDED,
						interactionID: toCancel,
						experienceName: pluginState.commonAttributes.experienceName,
						invokedFrom: pluginState.commonAttributes.invokedFrom,
						aiGroupId: pluginState.commonAttributes.aiGroupId,
						...(fg('platform_editor_ai_traceid_in_analytics_events') && {
							traceIds: pluginState.commonAttributes.traceIds,
						}),
						...(pluginState.commonAttributes.promptVersion &&
						fg('platform_editor_ai_promptversion_in_analytics')
							? {
									promptVersion: pluginState.commonAttributes.promptVersion,
								}
							: {}),
					});
					api?.analytics?.actions.fireAnalyticsEvent(discardedPayload);
				}

				window.clearTimeout(deferredAnalytics.deferred[toCancel]);

				const updatedDeferredAnalytics = {
					...deferredAnalytics,
					pending: omit(deferredAnalytics.deferred, [toCancel]),
					toCancel: undefined,
				};

				return newState.tr.setMeta(META_DEFERRED_ANALYTICS, updatedDeferredAnalytics);
			}

			if (!ps?.pendingAnalytics) {
				return null;
			}

			const {
				aiSessionId,
				nextActionType,
				aiOutputChars,
				aiOutputWords,
				nextActionRemovedAiChars,
				nextActionRemovedAiWords,
				nextActionTouchedAiRanges,
				nextActionLatencyMs,
				aiGroupId,
				experienceName,
			} = ps.pendingAnalytics;

			const tr = newState.tr.setMeta(META_FLUSH_ANALYTICS, true);
			return addAnalytics({
				editorState: newState,
				tr,
				payload: {
					action: 'updated',
					actionSubject: 'editorPluginAI',
					actionSubjectId: 'aiImmediateReaction',
					attributes: {
						aiSessionId,
						aiGroupId,
						nextActionType,
						aiOutputChars,
						aiOutputWords,
						nextActionRemovedAiChars,
						nextActionRemovedAiWords,
						nextActionTouchedAiRanges,
						nextActionLatencyMs,
						traceId: fg('platform_editor_ai_traceid_in_analytics_events')
							? decorationPluginState?.traceId
							: undefined,
						promptVersion: fg('platform_editor_ai_promptversion_in_analytics')
							? decorationPluginState?.promptVersion
							: undefined,
						experienceName,
					},
					eventType: EVENT_TYPE.TRACK,
				},
				addToHistory: false,
			});
		},
	});
};
